// BracePropsPanel.tsx — Right panel for Brace Plan view.
// Global defaults when 0 or multiple panels are checked.
// Per-panel override mode when exactly one panel is checked.

import type { PanelData, BracePlanSettings } from '../types/panelvar';

interface Props {
  globalSettings: BracePlanSettings;
  onChangeGlobal: (patch: Partial<BracePlanSettings>) => void;
  checkedPanelIds: Set<string>;
  allPanels: PanelData[];
  overrides: Record<string, Partial<BracePlanSettings>>;
  onSetOverride: (panelId: string, patch: Partial<BracePlanSettings>) => void;
  onClearOverride: (panelId: string) => void;
  braceCount: number;
  clashCount: number;
  clashBraceBrace: number;
  clashBracePanel: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function NumField({
  label, value, min, max, step = 1, unit,
  onChange,
}: {
  label: string; value: number; min: number; max: number; step?: number; unit?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="props-row">
      <label className="props-label">{label}</label>
      <div className="props-input-wrap" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type="number"
          className="props-input"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={e => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
          }}
          style={{ width: 64 }}
        />
        {unit && <span className="props-unit">{unit}</span>}
      </div>
    </div>
  );
}

export default function BracePropsPanel({
  globalSettings,
  onChangeGlobal,
  checkedPanelIds,
  allPanels,
  overrides,
  onSetOverride,
  onClearOverride,
  braceCount,
  clashCount,
  clashBraceBrace,
  clashBracePanel,
  collapsed,
  onToggleCollapse,
}: Props) {
  if (collapsed) {
    return (
      <div className="props-panel collapsed">
        <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title="Expand props">‹</button>
      </div>
    );
  }

  // Per-panel override mode when exactly one panel is checked
  const singlePanel = checkedPanelIds.size === 1
    ? allPanels.find(p => checkedPanelIds.has(p.id)) ?? null
    : null;

  const activeSettings: BracePlanSettings = singlePanel
    ? { ...globalSettings, ...(overrides[singlePanel.id] ?? {}) }
    : globalSettings;

  function handleChange(patch: Partial<BracePlanSettings>) {
    if (singlePanel) {
      onSetOverride(singlePanel.id, { ...(overrides[singlePanel.id] ?? {}), ...patch });
    } else {
      onChangeGlobal(patch);
    }
  }

  const hasOverride = singlePanel && Object.keys(overrides[singlePanel.id] ?? {}).length > 0;

  return (
    <div className="props-panel-inner">
      <div className="props-panel-header">
        <span className="props-panel-title">
          {singlePanel ? (singlePanel.mpvar.panelMark || singlePanel.id) : 'Brace Defaults'}
        </span>
        <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title="Collapse props">›</button>
      </div>

      {singlePanel && (
        <div className="props-section-label" style={{ padding: '4px 12px', color: '#4da6ff', fontSize: 11 }}>
          Per-panel override
          {hasOverride && (
            <button
              style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px' }}
              className="elev-sidebar-btn"
              onClick={() => onClearOverride(singlePanel.id)}
            >
              Reset to defaults
            </button>
          )}
        </div>
      )}

      <div className="props-section">
        {/* Inside / Outside toggle */}
        <div className="props-row">
          <label className="props-label">Brace side</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className={`elev-sidebar-btn${activeSettings.braceInterior !== false ? ' active' : ''}`}
              style={{ minWidth: 56, fontWeight: activeSettings.braceInterior !== false ? 700 : 400 }}
              onClick={() => handleChange({ braceInterior: true })}
            >
              Inside
            </button>
            <button
              className={`elev-sidebar-btn${activeSettings.braceInterior === false ? ' active' : ''}`}
              style={{ minWidth: 56, fontWeight: activeSettings.braceInterior === false ? 700 : 400 }}
              onClick={() => handleChange({ braceInterior: false })}
            >
              Outside
            </button>
          </div>
        </div>
        <NumField
          label="Brace angle"
          value={activeSettings.braceAngleDeg}
          min={35} max={68} step={1} unit="°"
          onChange={v => handleChange({ braceAngleDeg: v })}
        />
        <NumField
          label="Insert height"
          value={activeSettings.insertHeightPct}
          min={50} max={90} step={1} unit="% h"
          onChange={v => handleChange({ insertHeightPct: v })}
        />
        <NumField
          label="Max spacing"
          value={Math.round(activeSettings.maxSpacingIn / 12)}
          min={8} max={40} step={1} unit="LF"
          onChange={v => handleChange({ maxSpacingIn: v * 12 })}
        />
        <NumField
          label="Min braces"
          value={activeSettings.minBraceCount}
          min={1} max={6} step={1}
          onChange={v => handleChange({ minBraceCount: v })}
        />
      </div>

      {/* Summary */}
      <div className="props-section" style={{ borderTop: '1px solid #2a3a4a', paddingTop: 8 }}>
        <div className="props-row" style={{ flexDirection: 'column', gap: 4, fontSize: 12, color: '#aaa' }}>
          <div>{checkedPanelIds.size} panel{checkedPanelIds.size !== 1 ? 's' : ''} checked</div>
          <div>{braceCount} brace{braceCount !== 1 ? 's' : ''} computed</div>
          {clashCount > 0 ? (
            <div style={{ color: '#ff4400', fontWeight: 600 }}>
              ⚠ {clashCount} clash{clashCount !== 1 ? 'es' : ''}
              {clashBraceBrace > 0 && ` · ${clashBraceBrace} brace-brace`}
              {clashBracePanel > 0 && ` · ${clashBracePanel} brace-panel`}
            </div>
          ) : braceCount > 0 ? (
            <div style={{ color: '#44aa66' }}>✓ No clashes</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
